/**
 * Table of Contents Orchestrator
 *
 * Loads TOC sub-modules based on element presence.
 * Static imports ensure all modules bundle into single file.
 *
 * Features:
 * - Conditional initialization based on element presence
 * - Centralized cleanup management
 * - Single bundled output file
 *
 * Modules:
 * - heading-links: Main TOC functionality (adds IDs, generates links)
 * - progress-bar: Optional reading progress indicator
 */

import config from '@config';
import { getSelector, type ModuleConfig } from '@utils';
import { init as headingLinksInit } from './functions/heading-links.ts';
import { init as progressBarInit } from './functions/progress-bar.ts';

export async function init() {
  const moduleConfig = config.default.toc;
  const cleanup = {
    destroyFunctions: [] as Array<() => void>,
  };

  // Initialize heading-links if TOC list exists
  const headingLinksSelector = getSelector(moduleConfig as ModuleConfig, 'list');
  if (document.querySelector(headingLinksSelector)) {
    try {
      const result = await headingLinksInit(moduleConfig);
      if (result?.destroy) {
        cleanup.destroyFunctions.push(result.destroy);
      }
    } catch (error) {
      console.error('[toc] Error initializing heading-links:', error);
    }
  }

  // Initialize progress-bar if wrapper exists
  const progressBarSelector = getSelector(moduleConfig['progress-bar'] as ModuleConfig, 'wrapper');
  if (document.querySelector(progressBarSelector)) {
    try {
      const result = await progressBarInit({
        progressConfig: moduleConfig['progress-bar'],
        tocConfig: moduleConfig,
      } as any);
      if (result?.destroy) {
        cleanup.destroyFunctions.push(result.destroy);
      }
    } catch (error) {
      console.error('[toc] Error initializing progress-bar:', error);
    }
  }

  return {
    result: `toc initialized (${cleanup.destroyFunctions.length} modules active)`,
    destroy: () => {
      cleanup.destroyFunctions.forEach((destroyFn) => {
        try {
          destroyFn();
        } catch (error) {
          console.error('[toc] Error during cleanup:', error);
        }
      });
      cleanup.destroyFunctions.length = 0;
    },
  };
}
